import { http } from '@/utils/httpClient'

export interface QaTrendTrackerRecord {
  timestamp: string
  subTeam: string
  total: number
  active: number
  skipped: number
  ui: number
  api: number
  // Tests whose file has no UI/API tag or describe name — total - ui - api.
  // Absent on documents written before this field existed; not defaulted to 0
  // so the UI can tell "known zero" apart from "not tracked yet".
  other?: number
  buildUrl?: string
}

// Raw shape of a document returned by the ES search proxy (GET /jenkins-proxy/qa-trend-tracker) —
// written by the backend as `team`/`subTeam` (see qa-trend-tracker.service.ts's writeEsDocument
// call). `team` is the leadgen/revex/crm scope the request already filters to, not a per-record
// dimension, so it's dropped by the mapping below.
interface QaTrendTrackerEsRecord {
  timestamp: string
  team?: string
  subTeam?: string
  total: number
  active: number
  skipped: number
  ui: number
  api: number
  other?: number
  buildUrl?: string
}

export function mapEsRecordToTrendRecord(record: QaTrendTrackerEsRecord): QaTrendTrackerRecord {
  return {
    timestamp: record.timestamp,
    subTeam: record.subTeam ?? '',
    total: record.total,
    active: record.active,
    skipped: record.skipped,
    ui: record.ui,
    api: record.api,
    other: record.other,
    buildUrl: record.buildUrl,
  }
}

export interface QaTrendTrackerSubTeamSummary {
  subTeam: string
  total: number
  active: number
  skipped: number
  ui: number
  api: number
  other?: number
  lastUpdated: string
}

export interface QaTrendTrackerSummary {
  totals: {
    total: number
    active: number
    skipped: number
    ui: number
    api: number
    other?: number
  }
  bySubTeam: QaTrendTrackerSubTeamSummary[]
  /** Full time-series, newest first — used to render the Progress table. */
  history: QaTrendTrackerRecord[]
}

const EMPTY_SUMMARY: QaTrendTrackerSummary = {
  totals: { total: 0, active: 0, skipped: 0, ui: 0, api: 0, other: 0 },
  bySubTeam: [],
  history: [],
}

/**
 * Reduces the raw ES time-series (one record per subTeam per build) down to the
 * latest snapshot per subTeam, plus a sum across subTeams for top-line stats.
 */
export function summarizeQaTrendTracker(records: QaTrendTrackerRecord[]): QaTrendTrackerSummary {
  if (!Array.isArray(records) || records.length === 0) return EMPTY_SUMMARY

  const latestBySubTeam = new Map<string, QaTrendTrackerRecord>()
  for (const record of records) {
    const existing = latestBySubTeam.get(record.subTeam)
    if (!existing || new Date(record.timestamp) > new Date(existing.timestamp)) {
      latestBySubTeam.set(record.subTeam, record)
    }
  }

  const bySubTeam: QaTrendTrackerSubTeamSummary[] = Array.from(latestBySubTeam.values())
    .map(record => ({
      subTeam: record.subTeam,
      total: record.total,
      active: record.active,
      skipped: record.skipped,
      ui: record.ui,
      api: record.api,
      other: record.other,
      lastUpdated: record.timestamp,
    }))
    .sort((a, b) => a.subTeam.localeCompare(b.subTeam))

  const totals = bySubTeam.reduce(
    (acc, entry) => ({
      total: acc.total + entry.total,
      active: acc.active + entry.active,
      skipped: acc.skipped + entry.skipped,
      ui: acc.ui + entry.ui,
      api: acc.api + entry.api,
      // Any subTeam missing `other` (an older, pre-field ES doc) makes the total unknown too,
      // rather than silently under-counting it as 0.
      other: acc.other === undefined || entry.other === undefined ? undefined : acc.other + entry.other,
    }),
    { total: 0, active: 0, skipped: 0, ui: 0, api: 0, other: 0 as number | undefined }
  )

  const history = collapseToLatestPerDay(records)

  return { totals, bySubTeam, history }
}

/**
 * Collapses same-day builds down to the day's latest snapshot (per subTeam),
 * so the Progress table reads as one entry per day rather than one per build.
 */
function collapseToLatestPerDay(records: QaTrendTrackerRecord[]): QaTrendTrackerRecord[] {
  const dayKey = (timestamp: string) => new Date(timestamp).toDateString()
  const latestByDay = new Map<string, QaTrendTrackerRecord>()

  for (const record of records) {
    const key = `${record.subTeam}|${dayKey(record.timestamp)}`
    const existing = latestByDay.get(key)
    if (!existing || new Date(record.timestamp) > new Date(existing.timestamp)) {
      latestByDay.set(key, record)
    }
  }

  return Array.from(latestByDay.values()).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

export type QaTrendTrackerTeam = 'leadgen' | 'revex' | 'crm'
export type QaTrendTrackerJobState = 'in_progress' | 'completed' | 'failed'

export interface QaTrendTrackerTriggerResponse {
  success: boolean
  message: string
  data: { team: QaTrendTrackerTeam; projectName: string | null; status: string; triggeredAt: string }
}

export interface QaTrendTrackerStatusResponse {
  success: boolean
  data: {
    team: QaTrendTrackerTeam
    projectName: string | null
    status: QaTrendTrackerJobState
    startedAt: string
    finishedAt?: string
    totalProjects?: number
    processedProjects?: number
    error?: string
  }
}

export class QaTrendTrackerService {
  private readonly baseUrl = '/jenkins-proxy/qa-trend-tracker'
  private readonly generateUrl = '/qa-trend-tracker/generate'
  private readonly statusUrl = '/qa-trend-tracker/status'

  /**
   * @param projectName - Kaizen project name, used as the ES `subTeam` value to scope to. Omit to aggregate across all subTeams.
   * @param team - ES `team` value (leadgen/revex/crm). Only pass this when it's a known match — an unrelated team name would incorrectly hide real data.
   * @param startDate - ES range start, as a `YYYY-MM-DD` date string.
   * @param endDate - ES range end, as a `YYYY-MM-DD` date string. The backend derives the ES `size` from the startDate/endDate span.
   */
  async getSummary(projectName?: string, team?: string, startDate?: string, endDate?: string): Promise<QaTrendTrackerSummary> {
    const query: string[] = []
    if (projectName) query.push(`projectName=${encodeURIComponent(projectName)}`)
    if (team) query.push(`team=${encodeURIComponent(team)}`)
    if (startDate) query.push(`startDate=${encodeURIComponent(startDate)}`)
    if (endDate) query.push(`endDate=${encodeURIComponent(endDate)}`)
    const url = query.length ? `${this.baseUrl}?${query.join('&')}` : this.baseUrl
    const records = await http.get(url)
    return summarizeQaTrendTracker(Array.isArray(records) ? records.map(mapEsRecordToTrendRecord) : [])
  }

  /**
   * Kicks off background generation for one team; the backend responds before it finishes.
   * @param projectName - scopes generation to just this project instead of the whole team's repo.
   */
  async triggerGenerate(team: QaTrendTrackerTeam, projectName?: string): Promise<QaTrendTrackerTriggerResponse> {
    return http.post(this.generateUrl, { team, projectName })
  }

  /**
   * Looks up the status of the most recently triggered run for this team/project.
   * Rejects with a 404 if no run has ever been triggered for this combination.
   */
  async getStatus(team: QaTrendTrackerTeam, projectName?: string): Promise<QaTrendTrackerStatusResponse> {
    const query = projectName ? `?team=${encodeURIComponent(team)}&projectName=${encodeURIComponent(projectName)}` : `?team=${encodeURIComponent(team)}`
    return http.get(`${this.statusUrl}${query}`)
  }
}

export const qaTrendTrackerService = new QaTrendTrackerService()
